import { ColumnInfo, ColumnsResponse } from './types';
import { SchemaParseError } from './schemaSource';

const MAX_INPUT_CHARS = 1_000_000;
const SAMPLE_LIMIT = 10;
const MAX_DISTINCT_FOR_SAMPLES = 100;

const TYPE_NORMALIZATION: Record<string, string> = {
  varchar: 'character varying',
  'character varying': 'character varying',
  nvarchar: 'character varying',
  text: 'character varying',
  char: 'character',
  character: 'character',
  nchar: 'character',
  bpchar: 'character',
  smallint: 'smallint',
  int2: 'smallint',
  int: 'integer',
  integer: 'integer',
  int4: 'integer',
  bigint: 'bigint',
  int8: 'bigint',
  numeric: 'numeric',
  decimal: 'numeric',
  real: 'real',
  float4: 'real',
  float: 'double precision',
  float8: 'double precision',
  'double precision': 'double precision',
  boolean: 'boolean',
  bool: 'boolean',
  date: 'date',
  timestamp: 'timestamp without time zone',
  'timestamp without time zone': 'timestamp without time zone',
  timestamptz: 'timestamp with time zone',
  'timestamp with time zone': 'timestamp with time zone',
};

interface ParseResult {
  response: ColumnsResponse;
  warnings: string[];
}

function stripBomAndTrim(text: string): string {
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }
  return text.trim();
}

function detectFormat(text: string): 'json' | 'ddl' | 'csv' {
  const first = text[0];
  if (first === '[' || first === '{') return 'json';
  if (/CREATE\s+TABLE/i.test(text)) return 'ddl';
  return 'csv';
}

function normalizeType(rawType: string): { dataType: string; known: boolean } {
  const lower = rawType.toLowerCase().trim();
  // Strip length/precision e.g. varchar(100), numeric(38,4)
  const base = lower.replace(/\(.*\)/, '').trim();
  const normalized = TYPE_NORMALIZATION[base];
  if (normalized) return { dataType: normalized, known: true };
  return { dataType: base, known: false };
}

function stripQuotes(name: string): string {
  if (name.startsWith('"') && name.endsWith('"')) {
    return name.slice(1, -1);
  }
  return name;
}

function splitTopLevelCommas(body: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of body) {
    if (ch === '(') {
      depth++;
      current += ch;
    } else if (ch === ')') {
      depth--;
      current += ch;
    } else if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

function parseDDL(text: string): ParseResult {
  const warnings: string[] = [];

  // Count CREATE TABLE statements
  const createTableMatches = text.match(/CREATE\s+TABLE/gi);
  if (createTableMatches && createTableMatches.length > 1) {
    warnings.push(
      `${createTableMatches.length} tables detected; showing the first — paste one at a time to import another`,
    );
  }

  // Extract first CREATE TABLE
  const tableMatch = text.match(
    /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?((?:"[^"]+"|[^\s(]+)(?:\s*\.\s*(?:"[^"]+"|[^\s(]+))?)\s*\(/i,
  );
  if (!tableMatch) {
    throw new SchemaParseError('DDL_PARSE_ERROR', 'Could not find a valid CREATE TABLE statement with columns.');
  }

  const fullName = tableMatch[1];
  let schemaName: string | null = null;
  let tableName: string;

  if (fullName.includes('.')) {
    const dotIdx = fullName.indexOf('.');
    // Handle potential whitespace around dot
    const rawSchema = fullName.slice(0, dotIdx).trim();
    const rawTable = fullName.slice(dotIdx + 1).trim();
    schemaName = stripQuotes(rawSchema);
    tableName = stripQuotes(rawTable);
  } else {
    tableName = stripQuotes(fullName.trim());
  }

  // Extract the content between the outermost parens of the first CREATE TABLE
  const startParen = text.indexOf('(', tableMatch.index! + tableMatch[0].length - 1);
  let depth = 0;
  let endParen = -1;
  for (let i = startParen; i < text.length; i++) {
    if (text[i] === '(') depth++;
    else if (text[i] === ')') {
      depth--;
      if (depth === 0) {
        endParen = i;
        break;
      }
    }
  }

  if (endParen === -1) {
    throw new SchemaParseError('DDL_PARSE_ERROR', 'Could not find matching closing parenthesis for CREATE TABLE.');
  }

  const body = text.slice(startParen + 1, endParen);
  const parts = splitTopLevelCommas(body);

  // Table-level constraint patterns to skip
  const tableConstraintPattern =
    /^\s*(?:PRIMARY\s+KEY|UNIQUE|FOREIGN\s+KEY|CONSTRAINT|DISTSTYLE|DISTKEY|SORTKEY|COMPOUND\s+SORTKEY|INTERLEAVED\s+SORTKEY)/i;

  const columns: ColumnInfo[] = [];
  let ordinal = 1;

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed || tableConstraintPattern.test(trimmed)) continue;

    // Parse column: name type [attributes...]
    const colMatch = trimmed.match(/^("(?:[^"]|"")*"|[^\s]+)\s+(.+)$/s);
    if (!colMatch) continue;

    const columnName = stripQuotes(colMatch[1]);
    let rest = colMatch[2];

    // Extract type (may include parens like numeric(38,4))
    const typeMatch = rest.match(/^([a-zA-Z][a-zA-Z0-9_]*)(\([^)]*\))?\s*(.*)/s);
    if (!typeMatch) continue;

    // Handle multi-word types like DOUBLE PRECISION, TIMESTAMP WITHOUT TIME ZONE, etc.
    let rawTypeName = typeMatch[1].trim();
    let afterType = typeMatch[3] || '';

    // Check for multi-word type extensions
    const multiWordExtensions = [
      { pattern: /^(WITHOUT\s+TIME\s+ZONE)/i, prefix: 'timestamp' },
      { pattern: /^(WITH\s+TIME\s+ZONE)/i, prefix: 'timestamp' },
      { pattern: /^(PRECISION)/i, prefix: 'double' },
      { pattern: /^(VARYING)/i, prefix: 'character' },
    ];

    for (const ext of multiWordExtensions) {
      const extMatch = afterType.match(ext.pattern);
      if (extMatch && rawTypeName.toLowerCase().includes(ext.prefix)) {
        rawTypeName += ' ' + extMatch[1];
        afterType = afterType.slice(extMatch[0].length).trim();
        break;
      }
    }

    const { dataType, known } = normalizeType(rawTypeName);
    if (!known) {
      warnings.push(`Unknown type "${rawTypeName}" on column "${columnName}" — emitting as "${dataType}".`);
    }

    // Determine nullability from the remaining attributes
    const attributes = (typeMatch[2] || '') + ' ' + afterType;
    const hasNotNull = /NOT\s+NULL/i.test(attributes);
    const hasPrimaryKey = /PRIMARY\s+KEY/i.test(attributes);
    const isNullable = !(hasNotNull || hasPrimaryKey);

    columns.push({
      columnName,
      dataType,
      isNullable,
      ordinalPosition: ordinal++,
      distinctValues: null,
      sampleValues: null,
    });
  }

  if (columns.length === 0) {
    throw new SchemaParseError('DDL_PARSE_ERROR', 'No columns could be parsed from the CREATE TABLE statement.');
  }

  return { response: { tableName, schema: schemaName, columns }, warnings };
}

function splitCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        fields.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current);
  return fields;
}

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const INTEGER_REGEX = /^-?\d+$/;
const NUMERIC_REGEX = /^-?\d+(\.\d+)?$/;

function inferType(values: string[]): string {
  const nonEmpty = values.filter((v) => v !== '');
  if (nonEmpty.length === 0) return 'character varying';

  let allBoolean = true;
  let allInteger = true;
  let allNumeric = true;
  let hasDecimal = false;
  let allDate = true;

  for (const v of nonEmpty) {
    const lower = v.toLowerCase();
    if (lower !== 'true' && lower !== 'false') allBoolean = false;
    if (!INTEGER_REGEX.test(v) || /^-?0\d/.test(v)) allInteger = false;
    if (NUMERIC_REGEX.test(v)) {
      if (v.includes('.')) hasDecimal = true;
    } else {
      allNumeric = false;
    }
    if (!DATE_REGEX.test(v)) allDate = false;
  }

  // Leading-zero preservation: "0042" should stay character varying
  if (allBoolean) return 'boolean';
  if (allInteger) return 'integer';
  if (allNumeric && hasDecimal) return 'numeric';
  if (allDate) return 'date';
  return 'character varying';
}

function parseCSV(text: string, fileName?: string): ParseResult {
  const warnings: string[] = [];
  const allLines = text.split(/\r?\n/);
  // Remove trailing empty lines only; keep interior empty lines as they represent null/empty rows
  while (allLines.length > 0 && allLines[allLines.length - 1].trim() === '') {
    allLines.pop();
  }
  const lines = allLines;

  if (lines.length === 0 || lines.every((l) => l.trim() === '')) {
    throw new SchemaParseError('EMPTY_INPUT', 'Input is empty after removing blank lines.');
  }

  const headerFields = splitCSVLine(lines[0]);
  const headers = headerFields.map((h, i) => (h.trim() === '' ? `column_${i + 1}` : h.trim()));

  // Parse data rows
  const columnValues: string[][] = headers.map(() => []);

  for (let i = 1; i < lines.length; i++) {
    const fields = splitCSVLine(lines[i]);
    for (let col = 0; col < headers.length; col++) {
      const val = col < fields.length ? fields[col].trim() : '';
      columnValues[col].push(val);
    }
    if (fields.length > headers.length) {
      // ragged row warning only once
      if (!warnings.some((w) => w.includes('ragged'))) {
        warnings.push('Some rows have more fields than the header — extra fields ignored.');
      }
    }
  }

  const hasDataRows = lines.length > 1;
  const columns: ColumnInfo[] = headers.map((name, idx) => {
    const values = columnValues[idx];
    const nonEmpty = values.filter((v) => v !== '');
    const distinct = new Set(nonEmpty);
    const distinctCount = distinct.size;
    const isNullable = !hasDataRows || values.some((v) => v === '');
    const dataType = inferType(values);
    const sampleValues =
      hasDataRows && distinctCount > 0 && distinctCount <= MAX_DISTINCT_FOR_SAMPLES
        ? Array.from(distinct).slice(0, SAMPLE_LIMIT)
        : null;

    return {
      columnName: name,
      dataType,
      isNullable,
      ordinalPosition: idx + 1,
      distinctValues: hasDataRows ? distinctCount : null,
      sampleValues,
    };
  });

  const sanitized = (fileName || 'pasted_table').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
  const tName = /^[a-zA-Z_]/.test(sanitized) ? sanitized : `_${sanitized}`;

  return { response: { tableName: tName, schema: null, columns }, warnings };
}

function parseJSON(text: string, fileName?: string): ParseResult {
  const warnings: string[] = [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new SchemaParseError('JSON_PARSE_ERROR', 'Input looks like JSON but could not be parsed.');
  }

  let rows: Record<string, unknown>[];

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      throw new SchemaParseError('EMPTY_INPUT', 'JSON array is empty — no data to parse.');
    }
    rows = parsed as Record<string, unknown>[];
  } else if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    // Detect JSON Schema documents
    if (obj.type === 'object' && typeof obj.properties === 'object' && obj.properties !== null) {
      throw new SchemaParseError(
        'UNSUPPORTED_JSON',
        'This looks like a JSON Schema document, not data rows. Paste DDL or data rows instead.',
      );
    }
    rows = [obj];
  } else {
    throw new SchemaParseError('JSON_PARSE_ERROR', 'Input must be a JSON array or object.');
  }

  // Collect keys in first-seen order
  const keyOrder: string[] = [];
  const keySet = new Set<string>();
  for (const row of rows) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue;
    for (const key of Object.keys(row)) {
      if (!keySet.has(key)) {
        keySet.add(key);
        keyOrder.push(key);
      }
    }
  }

  if (keyOrder.length === 0) {
    throw new SchemaParseError('EMPTY_INPUT', 'JSON contains no usable keys.');
  }

  // Gather values per column
  const columnValues: string[][] = keyOrder.map(() => []);
  const columnNullable: boolean[] = keyOrder.map(() => false);

  for (const row of rows) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) continue;
    const obj = row as Record<string, unknown>;
    for (let i = 0; i < keyOrder.length; i++) {
      const key = keyOrder[i];
      if (!(key in obj) || obj[key] === null || obj[key] === undefined) {
        columnNullable[i] = true;
        columnValues[i].push('');
      } else {
        const val = obj[key];
        let strVal: string;
        if (typeof val === 'object') {
          strVal = JSON.stringify(val);
        } else {
          strVal = String(val);
        }
        columnValues[i].push(strVal);
      }
    }
  }

  const columns: ColumnInfo[] = keyOrder.map((key, idx) => {
    const values = columnValues[idx];
    const nonEmpty = values.filter((v) => v !== '');
    const distinct = new Set(nonEmpty);
    const distinctCount = distinct.size;
    const isNullable = columnNullable[idx];
    const dataType = inferType(values);
    const sampleValues =
      distinctCount > 0 && distinctCount <= MAX_DISTINCT_FOR_SAMPLES
        ? Array.from(distinct).slice(0, SAMPLE_LIMIT)
        : null;

    return {
      columnName: key,
      dataType,
      isNullable,
      ordinalPosition: idx + 1,
      distinctValues: distinctCount,
      sampleValues,
    };
  });

  const sanitized = (fileName || 'pasted_table').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
  const tName = /^[a-zA-Z_]/.test(sanitized) ? sanitized : `_${sanitized}`;

  return { response: { tableName: tName, schema: null, columns }, warnings };
}

/**
 * Parses DDL, CSV, or JSON text into a ColumnsResponse.
 * Throws SchemaParseError on failure.
 */
export function parseSchema(text: string): ColumnsResponse & { warnings: string[] } {
  const cleaned = stripBomAndTrim(text);

  if (cleaned.length === 0) {
    throw new SchemaParseError('EMPTY_INPUT', 'Input is empty or contains only whitespace.');
  }

  if (cleaned.length > MAX_INPUT_CHARS) {
    throw new SchemaParseError('INPUT_TOO_LARGE', `Input exceeds maximum of ${MAX_INPUT_CHARS.toLocaleString()} characters.`);
  }

  const format = detectFormat(cleaned);
  let result: ParseResult;

  switch (format) {
    case 'ddl':
      result = parseDDL(cleaned);
      break;
    case 'csv':
      result = parseCSV(cleaned);
      break;
    case 'json':
      result = parseJSON(cleaned);
      break;
  }

  // GAP-1 fix: forward non-fatal warnings on the returned object as an optional
  // field. The backend ColumnsResponse (Spec 1) simply omits warnings, so the
  // local/backend swap parity is preserved; SchemaImportStep reads response.warnings.
  return { ...result.response, warnings: result.warnings };
}
